import React from "react";
import { CheckCircle2, Palette, Type, Circle } from "lucide-react";
import { cn } from "../../lib/utils";
import type {
  DesignTokenOverrides,
  PrimaryPalette,
  AccentPalette,
  ButtonRadius,
} from "../../../types/design";
import {
  PRIMARY_HEX,
  ACCENT_HEX,
  SURFACE_BG_HEX,
  SURFACE_STYLE_LABELS,
  FONT_DISPLAY_NAMES,
  BUTTON_RADIUS_LABELS,
} from "../../../types/design";

type Props = {
  updates: DesignTokenOverrides;
  className?: string;
};

type ChangeRow = {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
};

export const DesignPreviewCard: React.FC<Props> = ({ updates, className }) => {
  if (!updates || Object.keys(updates).length === 0) return null;

  const rows: ChangeRow[] = [];

  const primaryColour: PrimaryPalette | undefined = updates.primary_palette;
  const accentColour: AccentPalette | undefined = updates.accent_palette;

  if (updates.surface_style) {
    const style = updates.surface_style;
    const bg = SURFACE_BG_HEX[style];
    rows.push({
      icon: (
        <Circle
          className="h-3.5 w-3.5 shrink-0"
          style={{
            fill: bg,
            stroke: "currentColor",
            color: "var(--color-border)",
          }}
        />
      ),
      label: "Background",
      value: (
        <span className="text-xs text-foreground">
          {SURFACE_STYLE_LABELS[style]}
        </span>
      ),
    });
  }

  if (primaryColour || accentColour) {
    rows.push({
      icon: <Palette className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />,
      label: "Brand colors",
      value: <ColourSwatches primary={primaryColour} accent={accentColour} />,
    });
  }

  if (updates.font_pairing) {
    const fonts = FONT_DISPLAY_NAMES[updates.font_pairing];
    rows.push({
      icon: <Type className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />,
      label: "Typography",
      value: (
        <span className="text-xs text-foreground">
          <span className="font-semibold">{fonts.heading}</span>
          <span className="text-muted-foreground"> · {fonts.body}</span>
        </span>
      ),
    });
  }

  if (updates.button_radius) {
    rows.push({
      icon: <RadiusIcon radius={updates.button_radius} />,
      label: "Buttons",
      value: (
        <span className="text-xs text-foreground">
          {buttonRadiusLabel(updates.button_radius)}
        </span>
      ),
    });
  }

  return (
    <div
      className={cn(
        "mt-2 overflow-hidden rounded-xl border border-border bg-card shadow-card",
        className,
      )}
    >
      <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-4 py-2.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-success" />
        <span className="text-xs font-semibold text-foreground">
          Design Applied
        </span>
        <span className="ml-auto rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success">
          Live on your site
        </span>
      </div>

      <div className="divide-y divide-border/50">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-2.5">
            {row.icon}
            <span className="w-20 shrink-0 text-xs text-muted-foreground">
              {row.label}
            </span>
            <div className="flex-1">{row.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ColourSwatches: React.FC<{
  primary?: PrimaryPalette;
  accent?: AccentPalette;
}> = ({ primary, accent }) => (
  <div className="flex items-center gap-2">
    <div className="flex items-center gap-1">
      {primary ? <Swatch hex={PRIMARY_HEX[primary]} label={primary} /> : null}
      {accent ? <Swatch hex={ACCENT_HEX[accent]} label={accent} /> : null}
    </div>
    <span className="text-xs text-foreground">
      {([primary, accent].filter(Boolean) as string[])
        .map(capitalise)
        .join(" + ")}
    </span>
  </div>
);

const Swatch: React.FC<{ hex: string; label: string }> = ({ hex, label }) => (
  <div
    className="h-4 w-4 rounded-full border border-black/10 shadow-sm"
    style={{ backgroundColor: hex }}
    title={label}
  />
);

function buttonRadiusLabel(radius: string): string {
  if (radius in BUTTON_RADIUS_LABELS) {
    return BUTTON_RADIUS_LABELS[radius as ButtonRadius];
  }
  if (radius === "sharp") return BUTTON_RADIUS_LABELS.square;
  if (radius === "pill") return BUTTON_RADIUS_LABELS.rounded;
  return radius;
}

const RadiusIcon: React.FC<{ radius: string }> = ({ radius }) => {
  const tight = radius === "square" || radius === "sharp" || radius === "pill";
  const r = tight ? "2px" : "6px";
  return (
    <div
      className="h-3.5 w-3.5 shrink-0 border-2 border-muted-foreground"
      style={{ borderRadius: r }}
    />
  );
};

function capitalise(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
